import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  AppPageBase,
  BreadcrumbService,
  GenreDiscItem,
  LsArtistsSectionComponent,
  LsGenresSectionComponent,
  LsPublicAlbumsSectionComponent,
  LsPublicPlaylistsSectionComponent,
  LsSectionBandComponent,
  RecueilService,
  EnumRouteName,
} from '@lyricstream/shared';
import { LyricListBandComponent } from '../../ui/lyric-list-band/lyric-list-band.component';
import { RecueilGridComponent } from '../../ui/recueil-grid/recueil-grid.component';

@Component({
  selector: 'app-discover',
  standalone: true,
  imports: [
    RouterLink,
    RecueilGridComponent,
    LyricListBandComponent,
    LsSectionBandComponent,
    LsArtistsSectionComponent,
    LsGenresSectionComponent,
    LsPublicAlbumsSectionComponent,
    LsPublicPlaylistsSectionComponent,
  ],
  templateUrl: './discover.html',
  styleUrl: './discover.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscoverComponent extends AppPageBase {
  protected readonly recueilService = inject(RecueilService);
  private  readonly breadcrumbService = inject(BreadcrumbService);

  protected readonly recueils = this.recueilService.recueils;
  protected readonly loading  = this.recueilService.loading;
  protected readonly error    = this.recueilService.error;

  constructor() {
    super();
    this.breadcrumbService.set([
      { label: 'Découvrir', routerLink: ['/', this.lang(), EnumRouteName.Discover] },
      { label: 'Recueils' },
    ]);
  }

  ngOnInit(): void {
    this.recueilService.getRecueilsLocal();
    //this.recueilService.getRecueilsActive();
  }

  protected readonly hymnes = [
    { ref: 'CEF 47', title: 'Adoration', year: '1881 · Domaine public' },
    { ref: 'CE 7', title: 'À Dieu soit la gloire', year: '1875 · Fanny Crosby' },
    { ref: 'CE 114', title: 'Quel ami fidèle et tendre', year: '1855 · Joseph Scriven' },
    { ref: 'SU 78', title: 'Plus près de toi, mon Dieu', year: '1841 · Sarah F. Adams' },
    { ref: 'CEF 58', title: 'Saint, Saint, Saint', year: '1826 · Reginald Heber' },
    { ref: 'CEF 134', title: 'À toi la gloire', year: '1884 · Edmond Budry' },
  ];

  protected readonly selections = [
    { label: '28 chants', count: '~2 h 14', title: 'Avent 2026', desc: 'Quatre semaines, quatre thèmes — Espérance, Paix, Joie, Amour. Pour préparer Noël.', curatorAv: 'EE', curatorStyle: '', curator: '@equipe-editoriale' },
    { label: '42 chants · krèyol', count: '~3 h', title: 'Réveil 2026', desc: "Les chants qui ont marqué les campagnes d'évangélisation et les services de minuit en Haïti cette année.", curatorAv: 'PJ', curatorStyle: 'background:linear-gradient(135deg,#1F8A5B,#FFE08A);color:#0E1116', curator: 'Pasteur Joel' },
    { label: '36 chants', count: '~2 h 40', title: 'Liturgie du dimanche matin', desc: "Adoration, confession, action de grâces, envoi — un déroulé classique en 4 mouvements pour les chefs de chœur.", curatorAv: 'CM', curatorStyle: 'background:linear-gradient(135deg,#6B3FA0,#FFB68A)', curator: 'Chœur Marie-Claire' },
    { label: '18 chants · paroles', count: '—', title: 'Funérailles : la consolation par les mots', desc: "Hymnes traditionnels pour services funèbres. Plusieurs sans audio, à interpréter sur orgue ou a cappella.", curatorAv: 'EE', curatorStyle: 'background:linear-gradient(135deg,#B53D11,#1456BE)', curator: '@equipe-editoriale' },
    { label: '24 chants', count: '~1 h 50', title: 'Pour les chorales débutantes', desc: "Tonalités accessibles (C, G, D), harmonies simples à 4 voix, durées courtes. Idéal pour apprendre.", curatorAv: 'RC', curatorStyle: 'background:linear-gradient(135deg,#142C6B,#FF6A1F)', curator: 'Marie-Rose Charles' },
    { label: '52 chants · krèyol+fr', count: '~3 h 40', title: "L'âme bilingue d'Haïti", desc: "Cantiques avec version krèyol et française côte à côte — pour les services qui mélangent les générations.", curatorAv: 'JD', curatorStyle: 'background:linear-gradient(135deg,#7E2A0C,#FF924F)', curator: 'Jean Duval' },
  ];

  /** Vrai quand la rubrique « Playlists publiques » affiche au moins une carte (décale l'alternance des bandes). */
  protected readonly hasPublicPlaylists = signal(false);
  protected readonly hasPublicAlbums = signal(false);


  /** Genres (données d'illustration, pas encore branchées sur le backend). */
  protected readonly genres: GenreDiscItem[] = [
    { name: 'Adoration', icon: 'ls-heart', count: '4 218' },
    { name: 'Hymnes', icon: 'ls-book', count: '2 840' },
    { name: 'Gospel', icon: 'ls-music', count: '1 924' },
    { name: 'Traditionnel', icon: 'ls-globe', count: '1 184' },
    { name: 'Compas gospel', icon: 'ls-users', count: '486' },
    { name: 'Contemporain', icon: 'ls-sparkles', count: '1 640' },
  ];

  protected readonly recueilsOld = [
    { sig: 'MJF', title: 'Mélodies Joyeuses', meta: '1 642 chants · krèyol', progress: 38, read: '62 lus' },
    { sig: 'CEF', title: "Chants d'Espérance — fr.", meta: '2 184 chants · français', progress: 14, read: '31 lus' },
    { sig: 'CE', title: "Chants d'Espérance — kr.", meta: '986 chants · krèyol', progress: 6, read: '14 lus' },
    { sig: 'JEM', title: "J'aime l'Éternel", meta: '714 chants · français', progress: 0, read: 'Pas encore commencé' },
  ];


}
